import { 
  collection, 
  doc, 
  addDoc, 
  setDoc,
  getDocs,
  getDoc, 
  updateDoc,
  deleteDoc, 
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db, storage, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { validateTags } from '../constants/dataTypes';
import {
  taskToRecordValues, recordPatchToTaskUpdate, recordValuesToNewTaskDoc,
} from '../shared/projectlyContract';

// Firestore rejects `undefined` values — recursively replace with null
function stripUndef(val) {
  if (Array.isArray(val)) return val.map(stripUndef);
  if (val !== null && typeof val === 'object') {
    const out = {};
    for (const k of Object.keys(val)) {
      out[k] = val[k] === undefined ? null : stripUndef(val[k]);
    }
    return out;
  }
  return val;
}

class DataService {
  constructor() {
    this.userId = null;
    this.workspaceId = null;
  }

  setUser(userId) {
    this.userId = userId;
  }

  // The workspace is the top-level container: it owns the pipeline, the
  // collections AND the projects. Set by AppLayout whenever the active
  // workspace changes; every project path below is relative to it.
  setWorkspace(workspaceId) {
    this.workspaceId = workspaceId;
  }

  // Path segments for the current workspace's projects. Spread into
  // collection()/doc() so nested paths (milestones, inputs, master_plans,
  // action_logs) all follow automatically.
  _proj() {
    if (!this.workspaceId) {
      // Failing loudly beats writing to workspaces/undefined/projects, which
      // Firestore accepts as a literal path segment and which is then
      // invisible to every reader.
      throw new Error('dataService: no active workspace set — call setWorkspace() first');
    }
    return ['workspaces', this.workspaceId, 'projects'];
  }

  // Projects
  async createProject(projectData) {
    const projectRef = await addDoc(collection(db, ...this._proj()), {
      ...projectData,
      userId: this.userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return projectRef.id;
  }

  async getProjects() {
    try {
      // Membership lives on the workspace now, so every project inside the
      // active workspace is visible to its members. The previous version
      // fetched the ENTIRE projects collection and filtered client-side, which
      // both leaked other tenants' project names and got slower with every
      // project anyone created.
      const snap = await getDocs(collection(db, ...this._proj()));
      return snap.docs
        .map(d => ({ id: d.id, ...d.data(), isOwner: d.data().userId === this.userId }))
        .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
    } catch (error) {
      console.error('Error getting projects:', error);
      return [];
    }
  }

  async updateProject(projectId, updates) {
    const projectRef = doc(db, ...this._proj(), projectId);
    await updateDoc(projectRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  async deleteProject(projectId) {
    const projectRef = doc(db, ...this._proj(), projectId);
    await deleteDoc(projectRef);
  }

  // Live project list for the active workspace. The mobile app reads these
  // same documents (projectly-app/src/services/projects.ts) — field names and
  // shapes must stay in lockstep with its toProject().
  subscribeProjects(callback) {
    return onSnapshot(
      collection(db, ...this._proj()),
      snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => { console.warn('[subscribeProjects]', err.message); callback([]); }
    );
  }

  // One project plus its tasks and milestones, kept live — mirrors mobile's
  // subscribeProject so both clients render from identical data.
  subscribeProject(projectId, callback) {
    let base = null;
    let tasks = [];
    let milestones = [];
    const emit = () => callback(base ? { ...base, tasks, milestones } : null);

    const unsubProject = onSnapshot(
      doc(db, ...this._proj(), projectId),
      snap => { base = snap.exists() ? { id: snap.id, ...snap.data() } : null; emit(); },
      err => { console.warn('[subscribeProject]', err.message); base = null; emit(); }
    );
    const unsubTasks = onSnapshot(
      collection(db, ...this._proj(), projectId, 'tasks'),
      snap => {
        tasks = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        emit();
      },
      err => { console.warn('[subscribeTasks]', err.message); tasks = []; emit(); }
    );
    const unsubMilestones = onSnapshot(
      query(collection(db, ...this._proj(), projectId, 'milestones'), orderBy('date')),
      snap => { milestones = snap.docs.map(d => ({ id: d.id, ...d.data() })); emit(); },
      // Milestones are optional; don't take the project view down with them.
      () => { milestones = []; emit(); }
    );

    return () => { unsubProject(); unsubTasks(); unsubMilestones(); };
  }

  // Just a project's tasks, kept live — the project rail needs per-project
  // progress without paying for the milestone listener subscribeProject adds.
  subscribeProjectTasks(projectId, callback) {
    return onSnapshot(
      collection(db, ...this._proj(), projectId, 'tasks'),
      snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => { console.warn('[subscribeProjectTasks]', err.message); callback([]); }
    );
  }

  // Tasks (sub-collection of projects). The contract, shared with mobile:
  // { title, status, assigneeId, start, due, priority, comments[] } where
  // start/due are ISO YYYY-MM-DD strings (NOT Timestamps) and priority is
  // 'High' | 'Medium' | 'Low'.
  async createTask(projectId, taskData) {
    const taskRef = await addDoc(
      collection(db, ...this._proj(), projectId, 'tasks'),
      {
        title: '',
        status: '',
        assigneeId: '',
        start: '',
        due: '',
        priority: 'Medium',
        comments: [],
        values: {},
        ...stripUndef(taskData),
        userId: this.userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    );
    return taskRef.id;
  }

  async updateTask(projectId, taskId, updates) {
    const taskRef = doc(db, ...this._proj(), projectId, 'tasks', taskId);
    await updateDoc(taskRef, {
      ...stripUndef(updates),
      _lastEditorUid: this.userId || null,   // so watcher notifications skip the actor
      updatedAt: serverTimestamp()
    });
  }

  // Subscribe/unsubscribe the current user to a task's update notifications.
  async setTaskWatching(projectId, taskId, watching) {
    const taskRef = doc(db, ...this._proj(), projectId, 'tasks', taskId);
    await updateDoc(taskRef, {
      watchers: watching ? arrayUnion(this.userId) : arrayRemove(this.userId),
      updatedAt: serverTimestamp()
    });
  }

  async deleteTask(projectId, taskId) {
    const taskRef = doc(db, ...this._proj(), projectId, 'tasks', taskId);
    await deleteDoc(taskRef);
  }

  // arrayUnion rather than read-modify-write: automations append comments to
  // the same array server-side, and last-write-wins would eat theirs.
  async addTaskComment(projectId, taskId, comment) {
    const taskRef = doc(db, ...this._proj(), projectId, 'tasks', taskId);
    await updateDoc(taskRef, {
      comments: arrayUnion(stripUndef(comment)),
      updatedAt: serverTimestamp()
    });
  }

  // Milestones (sub-collection of projects)
  async createMilestone(projectId, milestoneData) {
    const milestoneRef = await addDoc(
      collection(db, ...this._proj(), projectId, 'milestones'),
      {
        ...milestoneData,
        userId: this.userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    );
    return milestoneRef.id;
  }

  async getMilestones(projectId) {
    const q = query(
      collection(db, ...this._proj(), projectId, 'milestones'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  async updateMilestone(projectId, milestoneId, updates) {
    const milestoneRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId);
    await updateDoc(milestoneRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  async deleteMilestone(projectId, milestoneId) {
    const milestoneRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId);
    await deleteDoc(milestoneRef);
  }

  // Inputs (sub-collection of milestones) - AI responses
  async createInput(projectId, milestoneId, inputData) {
    const inputRef = await addDoc(
      collection(db, ...this._proj(), projectId, 'milestones', milestoneId, 'inputs'),
      {
        ...inputData,
        userId: this.userId,
        timestamp: serverTimestamp()
      }
    );
    return inputRef.id;
  }

  async getInputs(projectId, milestoneId) {
    const q = query(
      collection(db, ...this._proj(), projectId, 'milestones', milestoneId, 'inputs'),
      orderBy('timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  async updateInput(projectId, milestoneId, inputId, updates) {
    const inputRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId, 'inputs', inputId);
    await updateDoc(inputRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  async deleteInput(projectId, milestoneId, inputId) {
    const inputRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId, 'inputs', inputId);
    await deleteDoc(inputRef);
  }

  // Comments for inputs
  async addInputComment(projectId, milestoneId, inputId, comment) {
    const inputRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId, 'inputs', inputId);
    const inputDoc = await getDoc(inputRef);
    const currentComments = inputDoc.data()?.comments || [];
    await updateDoc(inputRef, {
      comments: [...currentComments, {
        text: comment,
        timestamp: new Date(),
        userId: this.userId
      }]
    });
  }

  // Master Plans (sub-collection of milestones) - Synthesized strategy
  async createMasterPlan(projectId, milestoneId, planData) {
    const planRef = await addDoc(
      collection(db, ...this._proj(), projectId, 'milestones', milestoneId, 'master_plans'),
      {
        ...planData,
        userId: this.userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    );
    return planRef.id;
  }

  async getMasterPlans(projectId, milestoneId) {
    const q = query(
      collection(db, ...this._proj(), projectId, 'milestones', milestoneId, 'master_plans'),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  async updateMasterPlan(projectId, milestoneId, planId, updates) {
    const planRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId, 'master_plans', planId);
    await updateDoc(planRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  async deleteMasterPlan(projectId, milestoneId, planId) {
    const planRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId, 'master_plans', planId);
    await deleteDoc(planRef);
  }

  // Action Logs (sub-collection of milestones) - Execution tracking
  async createActionLog(projectId, milestoneId, actionData) {
    // Validate tags before saving
    if (actionData.tags) {
      const { validTags, errors } = validateTags(actionData.tags);
      if (errors.length > 0) {
        throw new Error(`Invalid tags: ${errors.join(', ')}`);
      }
      actionData.tags = validTags;
    }

    const actionRef = await addDoc(
      collection(db, ...this._proj(), projectId, 'milestones', milestoneId, 'action_logs'),
      {
        ...actionData,
        userId: this.userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
    );
    return actionRef.id;
  }

  async getActionLogs(projectId, milestoneId) {
    const q = query(
      collection(db, ...this._proj(), projectId, 'milestones', milestoneId, 'action_logs'),
      orderBy('execution_date', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  async updateActionLog(projectId, milestoneId, actionId, updates) {
    // Validate tags if being updated
    if (updates.tags) {
      const { validTags, errors } = validateTags(updates.tags);
      if (errors.length > 0) {
        throw new Error(`Invalid tags: ${errors.join(', ')}`);
      }
      updates.tags = validTags;
    }

    const actionRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId, 'action_logs', actionId);
    await updateDoc(actionRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  async deleteActionLog(projectId, milestoneId, actionId) {
    const actionRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId, 'action_logs', actionId);
    await deleteDoc(actionRef);
  }

  // Comments for actions
  async addActionComment(projectId, milestoneId, actionId, comment) {
    const actionRef = doc(db, ...this._proj(), projectId, 'milestones', milestoneId, 'action_logs', actionId);
    const actionDoc = await getDoc(actionRef);
    const currentComments = actionDoc.data()?.comments || [];
    await updateDoc(actionRef, {
      comments: [...currentComments, {
        text: comment,
        timestamp: new Date(),
        userId: this.userId
      }]
    });
  }

  // Entities (root collection) - People/companies CRM
  async createEntity(entityData) {
    const entityRef = await addDoc(collection(db, 'entities'), {
      ...entityData,
      userId: this.userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return entityRef.id;
  }

  async getEntities() {
    const q = query(
      collection(db, 'entities'),
      where('userId', '==', this.userId)
    );
    const snapshot = await getDocs(q);
    const entities = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Client-side sorting by name
    return entities.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  async updateEntity(entityId, updates) {
    const entityRef = doc(db, 'entities', entityId);
    await updateDoc(entityRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  async deleteEntity(entityId) {
    const entityRef = doc(db, 'entities', entityId);
    await deleteDoc(entityRef);
  }

  // Analytics queries
  async getActionsByTag(tag) {
    // This would need to be implemented with Cloud Functions for complex queries
    // For now, we'll do client-side filtering
    const allProjects = await this.getProjects();
    const results = [];
    
    for (const project of allProjects) {
      const milestones = await this.getMilestones(project.id);
      for (const milestone of milestones) {
        const actions = await this.getActionLogs(project.id, milestone.id);
        const matchingActions = actions.filter(action => 
          action.tags && action.tags.includes(tag)
        );
        results.push(...matchingActions.map(action => ({
          ...action,
          projectName: project.name,
          milestoneName: milestone.objective_statement
        })));
      }
    }
    
    return results;
  }

  async getPerformanceByAISource() {
    // Client-side aggregation for now
    const allProjects = await this.getProjects();
    const sourcePerformance = {};
    
    for (const project of allProjects) {
      const milestones = await this.getMilestones(project.id);
      for (const milestone of milestones) {
        const inputs = await this.getInputs(project.id, milestone.id);
        const actions = await this.getActionLogs(project.id, milestone.id);
        
        // Simple correlation: if input from source X led to actions, track performance
        inputs.forEach(input => {
          if (!sourcePerformance[input.source]) {
            sourcePerformance[input.source] = {
              totalActions: 0,
              totalScore: 0,
              avgScore: 0
            };
          }
          
          // Find actions created after this input
          const relatedActions = actions.filter(action => 
            action.createdAt?.seconds > input.timestamp?.seconds &&
            action.outcome_score
          );
          
          relatedActions.forEach(action => {
            sourcePerformance[input.source].totalActions++;
            sourcePerformance[input.source].totalScore += action.outcome_score;
          });
        });
      }
    }
    
    // Calculate averages
    Object.keys(sourcePerformance).forEach(source => {
      const data = sourcePerformance[source];
      data.avgScore = data.totalActions > 0 ? data.totalScore / data.totalActions : 0;
    });
    
    return sourcePerformance;
  }

  // Export functionality
  async exportAllData() {
    const [projects, entities] = await Promise.all([
      this.getProjects(),
      this.getEntities()
    ]);

    const fullData = {
      projects: [],
      entities,
      exportDate: new Date().toISOString()
    };

    for (const project of projects) {
      const milestones = await this.getMilestones(project.id);
      const projectData = {
        ...project,
        milestones: []
      };

      for (const milestone of milestones) {
        const [inputs, masterPlans, actionLogs] = await Promise.all([
          this.getInputs(project.id, milestone.id),
          this.getMasterPlans(project.id, milestone.id),
          this.getActionLogs(project.id, milestone.id)
        ]);

        projectData.milestones.push({
          ...milestone,
          inputs,
          masterPlans,
          actionLogs
        });
      }

      fullData.projects.push(projectData);
    }

    return fullData;
  }

  // User Management
  async createOrUpdateUser(userId, email, username = null) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      // Check if user is admin
      const isAdmin = email === 'chris@splat.sh';
      
      if (!userDoc.exists()) {
        // Create new user record
        await setDoc(userRef, {
          email: email,
          username: username || email.split('@')[0], // Default to email prefix if no username
          createdAt: new Date(),
          lastActive: new Date(),
          aiAccess: isAdmin // Admins get AI access automatically
        });
      } else {
        // Update last active and ensure admin has AI access
        const updates = {
          lastActive: new Date()
        };
        
        // Add username if provided and not already set
        if (username && !userDoc.data().username) {
          updates.username = username;
        }
        
        // Ensure admin always has AI access
        if (isAdmin && !userDoc.data().aiAccess) {
          updates.aiAccess = true;
        }
        
        await updateDoc(userRef, updates);
      }
    } catch (error) {
      console.error('Error creating/updating user:', error);
    }
  }

  async getUserData(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting user data:', error);
      throw error;
    }
  }

  async updateNotificationPreferences(userId, prefs) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        notificationPreferences: prefs,
        notificationPrefsUpdatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  async updateContactInfo(userId, contactInfo) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        contactInfo,
        contactInfoUpdatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error updating contact info:', error);
      throw error;
    }
  }

  // Admin Methods
  async getAllUsers() {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Remove duplicates and ensure unique users
      const uniqueUsers = users.filter((user, index, self) => 
        index === self.findIndex(u => u.email === user.email)
      );
      
      return uniqueUsers;
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  }

  async updateUserAiAccess(userId, hasAccess) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        aiAccess: hasAccess,
        aiAccessUpdatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating user AI access:', error);
      throw error;
    }
  }

  async requestAiAccess(userId, userEmail, message = '') {
    try {
      await addDoc(collection(db, 'ai_access_requests'), {
        userId: userId,
        userEmail: userEmail,
        message: message,
        createdAt: new Date(),
        status: 'pending'
      });
    } catch (error) {
      console.error('Error requesting AI access:', error);
      throw error;
    }
  }

  async getAiAccessRequests() {
    try {
      const requestsSnapshot = await getDocs(
        query(
          collection(db, 'ai_access_requests'),
          where('status', '==', 'pending')
        )
      );
      const requests = requestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort in memory to avoid index requirement
      return requests.sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());
    } catch (error) {
      console.error('Error getting AI access requests:', error);
      throw error;
    }
  }

  async removeAiAccessRequest(requestId) {
    try {
      await deleteDoc(doc(db, 'ai_access_requests', requestId));
    } catch (error) {
      console.error('Error removing AI access request:', error);
      throw error;
    }
  }

  async getUserAiAccess(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      return userDoc.exists() ? userDoc.data().aiAccess || false : false;
    } catch (error) {
      console.error('Error getting user AI access:', error);
      return false;
    }
  }

  async checkUserHasRequestedAiAccess(userId) {
    try {
      const requestsSnapshot = await getDocs(
        query(
          collection(db, 'ai_access_requests'),
          where('userId', '==', userId),
          where('status', '==', 'pending')
        )
      );
      return !requestsSnapshot.empty;
    } catch (error) {
      console.error('Error checking AI access requests:', error);
      return false;
    }
  }

  // AI Usage Tracking
  async trackAiUsage(userId, type) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const usage = userData.aiUsage || {
          masterPlansGenerated: 0,
          actionItemsGenerated: 0,
          totalAiRequests: 0
        };

        switch(type) {
          case 'masterPlan':
            usage.masterPlansGenerated = (usage.masterPlansGenerated || 0) + 1;
            break;
          case 'actionItems':
            usage.actionItemsGenerated = (usage.actionItemsGenerated || 0) + 1;
            break;
          default:
            console.warn(`Unknown AI usage type: ${type}`);
            break;
        }
        
        usage.totalAiRequests = (usage.totalAiRequests || 0) + 1;
        usage.lastAiUsage = new Date();

        await updateDoc(userRef, {
          aiUsage: usage
        });
      }
    } catch (error) {
      console.error('Error tracking AI usage:', error);
    }
  }

  // Workspaces
  async createWorkspace(name, description = '') {
    const ref = await addDoc(collection(db, 'workspaces'), {
      name,
      description,
      userId: this.userId,
      members: [this.userId],
      memberRoles: { [this.userId]: 'owner' },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return ref.id;
  }

  async getWorkspaces() {
    const [ownedSnap, memberSnap] = await Promise.all([
      getDocs(query(collection(db, 'workspaces'), where('userId', '==', this.userId))),
      getDocs(query(collection(db, 'workspaces'), where('members', 'array-contains', this.userId))),
    ]);
    const byId = {};
    ownedSnap.docs.forEach(d => { byId[d.id] = { id: d.id, ...d.data(), isOwner: true }; });
    memberSnap.docs.forEach(d => {
      if (!byId[d.id]) byId[d.id] = { id: d.id, ...d.data(), isOwner: false };
    });
    return Object.values(byId).sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
  }

  subscribeWorkspace(workspaceId, callback) {
    if (!workspaceId) return () => {};
    const ref = doc(db, 'workspaces', workspaceId);
    return onSnapshot(ref,
      snap => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      err => console.error('[subscribeWorkspace]', err)
    );
  }

  // Deliver a direct message to a fellow workspace member. Server-side: only
  // the owner may write a user's inbox, so the callable does the delivery
  // after checking both people share the workspace.
  async sendUserMessage(workspaceId, toUid, subject, body, threadId) {
    const call = httpsCallable(functions, 'sendUserMessage');
    const res = await call({ workspaceId, toUid, subject, body, threadId });
    return res.data;
  }

  // Server-side join: fellow members need each other's names, but the rules
  // deny clients reading other users' documents. The callable does the lookup
  // with the Admin SDK, gated on the caller being a member.
  async getWorkspaceMembers(workspaceId) {
    try {
      const call = httpsCallable(functions, 'getWorkspaceMembersInfo');
      const res = await call({ workspaceId });
      return res.data?.members || [];
    } catch (e) {
      console.warn('[getWorkspaceMembers]', e.message);
      return [];
    }
  }

  async setMemberRole(workspaceId, uid, role) {
    const ref = doc(db, 'workspaces', workspaceId);
    await updateDoc(ref, {
      [`memberRoles.${uid}`]: role,
      updatedAt: serverTimestamp(),
    });
  }

  // Server-side: looking a user up by email means reading someone else's user
  // document, which the rules correctly deny to clients. The callable does the
  // lookup with the Admin SDK behind the manage_members capability.
  async addMemberByEmail(workspaceId, email, role = 'viewer') {
    try {
      const call = httpsCallable(functions, 'addWorkspaceMember');
      const res = await call({ workspaceId, email, role });
      return res.data?.uid;
    } catch (e) {
      throw new Error(e.message || 'Could not invite member.');
    }
  }

  async removeMember(workspaceId, uid) {
    const wsSnap = await getDoc(doc(db, 'workspaces', workspaceId));
    const data = wsSnap.data() || {};
    if (data.userId === uid) throw new Error('Cannot remove the workspace owner.');
    const members = (data.members || []).filter(m => m !== uid);
    const memberRoles = { ...(data.memberRoles || {}) };
    delete memberRoles[uid];
    await updateDoc(doc(db, 'workspaces', workspaceId), {
      members,
      memberRoles,
      updatedAt: serverTimestamp(),
    });
  }

  async updateWorkspace(workspaceId, updates) {
    await updateDoc(doc(db, 'workspaces', workspaceId), { ...updates, updatedAt: serverTimestamp() });
  }

  async deleteWorkspace(workspaceId) {
    await deleteDoc(doc(db, 'workspaces', workspaceId));
  }

  // Collection nodes may be LINKED to a project's tasks (data.linkedProjectId)
  // — the record methods below then read/write the task documents instead of
  // a records subcollection. The map is refreshed on every pipeline read, so
  // any screen that loaded the pipeline first (Data view, canvas modals) gets
  // linkage-aware records transparently.
  _refreshLinkMap(pipelineData) {
    const links = {};
    (pipelineData?.nodes || []).forEach(n => {
      if (n.type === 'collectionNode' && n.data?.linkedProjectId) {
        links[n.id] = n.data.linkedProjectId;
      }
    });
    this._linkedCollections = links;
  }

  _linkedProject(collectionId) {
    return (this._linkedCollections || {})[collectionId] || null;
  }

  _tasksCol(workspaceId, projectId) {
    return collection(db, `workspaces/${workspaceId}/projects/${projectId}/tasks`);
  }

  // Pipeline (nodes + edges for a workspace)
  async getPipeline(workspaceId) {
    const ref = doc(db, 'workspaces', workspaceId, 'pipeline', 'main');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      this._refreshLinkMap(data);
      return data;
    }
    return { nodes: [], edges: [] };
  }

  // Live subscription to the pipeline doc. Used so the canvas reflects writes
  // made by the server-side workflow engine (field updates, tasks, gate approvals).
  subscribePipeline(workspaceId, callback) {
    const ref = doc(db, 'workspaces', workspaceId, 'pipeline', 'main');
    return onSnapshot(
      ref,
      snap => { if (snap.exists()) { this._refreshLinkMap(snap.data()); callback(snap.data()); } },
      err => console.error('[subscribePipeline]', err)
    );
  }

  subscribePipelineLogs(workspaceId, callback) {
    const q = query(
      collection(db, 'workspaces', workspaceId, 'pipeline_logs'),
      orderBy('timestamp', 'desc'),
      limit(100)
    );
    return onSnapshot(
      q,
      snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => console.error('[subscribePipelineLogs]', err)
    );
  }

  async savePipeline(workspaceId, nodes, edges, active) {
    const ref = doc(db, 'workspaces', workspaceId, 'pipeline', 'main');
    const payload = { nodes: stripUndef(nodes), edges: stripUndef(edges), updatedAt: serverTimestamp() };
    if (active !== undefined) payload.active = active;
    // Merge so routine saves (node moves, edits) don't wipe the `active` flag —
    // a non-merge setDoc would delete any field not present in the payload,
    // silently disabling all server-side triggers on the next canvas edit.
    await setDoc(ref, payload, { merge: true });
  }

  // Live list of public forms for one collection (managed via the
  // createPublicForm / setPublicFormEnabled / deletePublicForm callables).
  subscribePublicForms(workspaceId, collectionId, callback) {
    const q = query(
      collection(db, 'public_forms'),
      where('workspaceId', '==', workspaceId),
      where('collectionId', '==', collectionId)
    );
    return onSnapshot(
      q,
      snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => console.error('[subscribePublicForms]', err)
    );
  }

  // Append a node to the pipeline doc (used by the Tables view to create a
  // collection without opening the canvas). Reads fresh state at call time to
  // minimise clobbering a concurrently-open canvas.
  async addPipelineNode(workspaceId, node) {
    const p = await this.getPipeline(workspaceId);
    await this.savePipeline(workspaceId, [...(p.nodes || []), node], p.edges || []);
  }

  // Merge updates into one node's data (e.g. rename a collection or edit its
  // fields from the Tables view) without touching the rest of the pipeline.
  async updatePipelineNodeData(workspaceId, nodeId, dataUpdates) {
    const p = await this.getPipeline(workspaceId);
    const nodes = (p.nodes || []).map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, ...dataUpdates } } : n
    );
    await this.savePipeline(workspaceId, nodes, p.edges || []);
  }

  // Deploy a pre-built pipeline (labs, starter template). Records are no longer
  // embedded in the pipeline doc — any sample records on collection nodes are
  // peeled off and written into each collection's records subcollection so they
  // actually show up on the canvas after deploy.
  async deployPipeline(workspaceId, nodes, edges, active) {
    const cleanNodes = [];
    const seeds = [];
    for (const n of nodes) {
      if (n.type === 'collectionNode' && Array.isArray(n.data?.records) && n.data.records.length) {
        const { records, ...restData } = n.data;
        cleanNodes.push({ ...n, data: restData });
        seeds.push({ collectionId: n.id, records });
      } else {
        cleanNodes.push(n);
      }
    }
    await this.savePipeline(workspaceId, cleanNodes, edges);
    // Seed sample records BEFORE activating so demo data doesn't fire triggers.
    for (const s of seeds) {
      await this.seedRecords(workspaceId, s.collectionId, s.records);
    }
    // Activate so server-side triggers actually fire, and register any schedules
    // in active_pipelines (which the scheduler reads). Without this, a deployed
    // pipeline saves with no `active` flag and every Cloud Function trigger bails.
    if (active) {
      await this.setPipelineActive(workspaceId, true, cleanNodes);
    }
    return cleanNodes;
  }

  // Write provided records into a collection's subcollection, preserving their
  // ids so reference fields keep pointing at the right rows.
  async seedRecords(workspaceId, collectionId, records) {
    for (const r of records) {
      const values = stripUndef(r.values || {});
      if (r.id) {
        await setDoc(
          doc(db, `workspaces/${workspaceId}/collections/${collectionId}/records`, r.id),
          // _seeded marks demo/sample data so server triggers skip it — otherwise
          // seeding sample rows into an active pipeline fires notifications for them.
          { values, _seeded: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }
        );
      } else {
        await setDoc(
          doc(collection(db, `workspaces/${workspaceId}/collections/${collectionId}/records`)),
          { values, _seeded: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }
        );
      }
    }
  }

  async setPipelineActive(workspaceId, active, nodes = []) {
    const ref = doc(db, 'workspaces', workspaceId, 'pipeline', 'main');
    await updateDoc(ref, { active, updatedAt: serverTimestamp() });

    const apRef = doc(db, 'active_pipelines', workspaceId);
    if (active) {
      const scheduleTriggers = nodes
        .filter(n => n.type === 'triggerNode' && n.data?.triggerType === 'schedule')
        .map(n => ({
          nodeId: n.id,
          intervalSec: Math.max(parseInt(n.data?.scheduleInterval || '60', 10), 10),
          lastRunAt: 0,
        }));
      await setDoc(apRef, {
        workspaceId,
        ownerId: this.userId,
        scheduleTriggers,
        activatedAt: serverTimestamp(),
      });
    } else {
      await deleteDoc(apRef).catch(() => {});
    }
  }

  // External service credentials (GitHub PAT, etc.) per workspace
  async getConnections(workspaceId) {
    try {
      const ref = doc(db, 'workspaces', workspaceId, 'connections', 'main');
      const snap = await getDoc(ref);
      return snap.exists() ? snap.data() : {};
    } catch { return {}; }
  }

  async saveConnections(workspaceId, connections) {
    const ref = doc(db, 'workspaces', workspaceId, 'connections', 'main');
    await setDoc(ref, { ...connections, updatedAt: serverTimestamp() });
  }

  async getUserAiUsage(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data().aiUsage || {
          masterPlansGenerated: 0,
          actionItemsGenerated: 0,
          totalAiRequests: 0
        };
      }
      return { masterPlansGenerated: 0, actionItemsGenerated: 0, totalAiRequests: 0 };
    } catch (error) {
      console.error('Error getting user AI usage:', error);
      return { masterPlansGenerated: 0, actionItemsGenerated: 0, totalAiRequests: 0 };
    }
  }

  // Theme Management
  async saveUserTheme(userId, theme) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        theme: theme,
        themeUpdatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving user theme:', error);
      throw error;
    }
  }

  async getUserTheme(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data().theme || 'light';
      }
      return 'light';
    } catch (error) {
      console.error('Error getting user theme:', error);
      return 'light';
    }
  }

  // Project Collaboration
  async inviteUserToProject(projectId, userEmail, invitedBy) {
    try {
      // Check if user exists
      const usersSnapshot = await getDocs(
        query(collection(db, 'users'), where('email', '==', userEmail))
      );
      
      if (usersSnapshot.empty) {
        throw new Error('User not found');
      }

      const userData = usersSnapshot.docs[0];
      const userId = userData.id;
      
      // Check if already invited or member
      const existingInvite = await getDocs(
        query(
          collection(db, 'project_invitations'),
          where('projectId', '==', projectId),
          where('userId', '==', userId)
        )
      );

      if (!existingInvite.empty) {
        throw new Error('User already invited to this project');
      }

      // Create invitation
      await addDoc(collection(db, 'project_invitations'), {
        projectId: projectId,
        userId: userId,
        userEmail: userEmail,
        invitedBy: invitedBy,
        status: 'pending',
        createdAt: new Date()
      });

      return true;
    } catch (error) {
      console.error('Error inviting user:', error);
      throw error;
    }
  }

  async getProjectInvitations(userId) {
    try {
      const invitationsSnapshot = await getDocs(
        query(
          collection(db, 'project_invitations'),
          where('userId', '==', userId),
          where('status', '==', 'pending')
        )
      );

      const invitations = [];
      for (const inviteDoc of invitationsSnapshot.docs) {
        const invitation = { id: inviteDoc.id, ...inviteDoc.data() };
        
        // Get project details
        const projectDoc = await getDoc(doc(db, ...this._proj(), invitation.projectId));
        if (projectDoc.exists()) {
          invitation.project = projectDoc.data();
        }

        invitations.push(invitation);
      }

      return invitations;
    } catch (error) {
      console.error('Error getting invitations:', error);
      return [];
    }
  }

  async respondToInvitation(invitationId, accept) {
    try {
      const inviteRef = doc(db, 'project_invitations', invitationId);
      const inviteDoc = await getDoc(inviteRef);
      
      if (!inviteDoc.exists()) {
        throw new Error('Invitation not found');
      }

      const inviteData = inviteDoc.data();

      if (accept) {
        // Add user to project members
        const projectRef = doc(db, ...this._proj(), inviteData.projectId);
        const projectDoc = await getDoc(projectRef);
        
        if (projectDoc.exists()) {
          const currentMembers = projectDoc.data().members || [];
          const newMembers = [...currentMembers, inviteData.userId];
          
          await updateDoc(projectRef, {
            members: newMembers,
            updatedAt: serverTimestamp()
          });
        }

        // Mark invitation as accepted
        await updateDoc(inviteRef, {
          status: 'accepted',
          respondedAt: new Date()
        });
      } else {
        // Mark invitation as declined
        await updateDoc(inviteRef, {
          status: 'declined',
          respondedAt: new Date()
        });
      }

      return true;
    } catch (error) {
      console.error('Error responding to invitation:', error);
      throw error;
    }
  }

  /**
   * Members of the workspace the project lives in.
   *
   * Membership moved from the project to the workspace — a workspace has one
   * member list and everyone in it can see every project inside it. The
   * `projectId` argument is kept so the ~6 call sites don't all have to change,
   * but it is deliberately unused: there is no per-project member list any more.
   *
   * NOTE: the "remove member" action in ProjectMembersModal / ProjectManageModal
   * therefore removes someone from the WHOLE workspace, not one project. Their
   * copy still says "project" and needs updating.
   */
  async getProjectMembers(_projectId) {
    try {
      if (!this.workspaceId) return [];
      const wsDoc = await getDoc(doc(db, 'workspaces', this.workspaceId));
      if (!wsDoc.exists()) return [];

      const data = wsDoc.data();
      const ownerId = data.userId;
      const roles = data.memberRoles || {};
      const memberIds = [...(data.members || [])];
      if (ownerId && !memberIds.includes(ownerId)) memberIds.unshift(ownerId);

      const members = [];
      for (const memberId of memberIds) {
        const userDoc = await getDoc(doc(db, 'users', memberId));
        if (userDoc.exists()) {
          const role = roles[memberId] || (memberId === ownerId ? 'owner' : 'member');
          members.push({
            id: memberId,
            ...userDoc.data(),
            isOwner: memberId === ownerId,
            isAdmin: role === 'admin' || role === 'owner',
            role: role === 'owner' ? 'Owner' : role === 'admin' ? 'Admin' : 'Member',
          });
        }
      }
      return members;
    } catch (error) {
      console.error('Error getting workspace members:', error);
      return [];
    }
  }

  /** Removes someone from the workspace (see getProjectMembers note). */
  async removeProjectMember(_projectId, memberId) {
    try {
      if (!this.workspaceId) throw new Error('no active workspace');
      const wsRef = doc(db, 'workspaces', this.workspaceId);
      const snap = await getDoc(wsRef);
      if (!snap.exists()) throw new Error('workspace not found');

      const data = snap.data();
      if (data.userId === memberId) throw new Error('Cannot remove the workspace owner');

      const roles = { ...(data.memberRoles || {}) };
      delete roles[memberId];
      await updateDoc(wsRef, {
        members: (data.members || []).filter(id => id !== memberId),
        memberRoles: roles,
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (error) {
      console.error('Error removing workspace member:', error);
      throw error;
    }
  }

  /** Promotes/demotes within the workspace (see getProjectMembers note). */
  async updateMemberRole(_projectId, memberId, isAdmin) {
    try {
      if (!this.workspaceId) throw new Error('no active workspace');
      const wsRef = doc(db, 'workspaces', this.workspaceId);
      const snap = await getDoc(wsRef);
      if (!snap.exists()) throw new Error('workspace not found');
      if (snap.data().userId === memberId) throw new Error("Cannot change the owner's role");

      await updateDoc(wsRef, {
        [`memberRoles.${memberId}`]: isAdmin ? 'admin' : 'member',
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (error) {
      console.error('Error updating member role:', error);
      throw error;
    }
  }

  async transferProjectOwnership(projectId, newOwnerId) {
    try {
      const projectRef = doc(db, ...this._proj(), projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const currentMembers = projectDoc.data().members || [];
      const currentAdmins = projectDoc.data().admins || [];
      
      // Ensure new owner is a member
      if (!currentMembers.includes(newOwnerId)) {
        throw new Error('New owner must be a project member');
      }

      // Remove new owner from regular members and admins lists
      const newMembers = currentMembers.filter(id => id !== newOwnerId);
      const newAdmins = currentAdmins.filter(id => id !== newOwnerId);
      
      // Add current owner to members list
      const currentOwnerId = projectDoc.data().userId;
      newMembers.push(currentOwnerId);

      await updateDoc(projectRef, {
        userId: newOwnerId,
        members: newMembers,
        admins: newAdmins,
        updatedAt: serverTimestamp(),
        ownershipTransferredAt: new Date(),
        previousOwner: currentOwnerId
      });

      return true;
    } catch (error) {
      console.error('Error transferring ownership:', error);
      throw error;
    }
  }

  // Global API Key Management
  /**
   * Store the shared Claude API key.
   *
   * Goes through the setClaudeApiKey callable, which verifies the caller is an
   * admin server-side and writes it to Secret Manager. The client used to
   * setDoc() straight into config/global — which is exactly why any signed-in
   * user could then read the key back out of it. There is deliberately no
   * getGlobalApiKey(): the key is write-only from the client.
   */
  async saveGlobalApiKey(apiKey) {
    const clean = (apiKey || '').trim();
    if (!clean.startsWith('sk-ant-')) {
      throw new Error('That does not look like a Claude API key (expected it to start with sk-ant-).');
    }
    await httpsCallable(functions, 'setClaudeApiKey')({ apiKey: clean });
    return true;
  }

  // ── Documents (user file upload library) ──────────────────────────────────

  subscribeDocuments(workspaceId, callback) {
    if (!workspaceId) return () => {};
    const q = query(
      collection(db, `workspaces/${workspaceId}/documents`),
      orderBy('uploadedAt', 'desc')
    );
    return onSnapshot(q, (snap) => {
      callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }

  uploadDocument(workspaceId, file, folder, onProgress) {
    const safeName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const storagePath = `workspaces/${workspaceId}/documents/${safeName}`;
    const storageRef = ref(storage, storagePath);
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type });

    return new Promise((resolve, reject) => {
      task.on('state_changed',
        (snap) => {
          if (onProgress) onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        },
        (err) => reject(err),
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          const docRef = await addDoc(
            collection(db, `workspaces/${workspaceId}/documents`),
            {
              name: file.name,
              storagePath,
              url,
              contentType: file.type,
              size: file.size,
              folder: folder || 'General',
              uploadedBy: this.userId,
              uploadedAt: serverTimestamp(),
            }
          );
          resolve({ id: docRef.id, name: file.name, url, storagePath, contentType: file.type, size: file.size, folder: folder || 'General' });
        }
      );
    });
  }

  async renameDocument(workspaceId, docId, newName) {
    await updateDoc(doc(db, `workspaces/${workspaceId}/documents`, docId), { name: newName });
  }

  async moveDocument(workspaceId, docId, newFolder) {
    await updateDoc(doc(db, `workspaces/${workspaceId}/documents`, docId), { folder: newFolder });
  }

  async deleteDocument(workspaceId, docId, storagePath) {
    try { await deleteObject(ref(storage, storagePath)); } catch (_) { /* file may already be gone */ }
    await deleteDoc(doc(db, `workspaces/${workspaceId}/documents`, docId));
  }

  // ── Workspace API keys ────────────────────────────────────────────────────
  // Only key metadata is read client-side (name, prefix, expiry, usage). The
  // raw key is generated, hashed, and returned once by the generateApiKey
  // callable — it is never stored or readable afterwards.

  subscribeApiKeys(workspaceId, callback) {
    if (!workspaceId) return () => {};
    const q = query(
      collection(db, 'apiKeys'),
      where('workspaceId', '==', workspaceId)
    );
    return onSnapshot(
      q,
      (snap) => {
        const keys = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        keys.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        callback(keys);
      },
      (err) => console.error('[subscribeApiKeys]', err)
    );
  }

  // ── Inbound task connectors ───────────────────────────────────────────────
  // One row per connected sender, written by the API when tasks arrive and
  // removed when that sender disconnects. This is what tells the Connectors
  // screen a link is live — see ConnectionsView for why key usage cannot.

  subscribeTaskConnectors(workspaceId, callback) {
    if (!workspaceId) return () => {};
    return onSnapshot(
      collection(db, `workspaces/${workspaceId}/taskConnectors`),
      (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error('[subscribeTaskConnectors]', err)
    );
  }

  // ── Collection records (subcollection) ────────────────────────────────────

  _recordsCol(workspaceId, collectionId) {
    return collection(db, `workspaces/${workspaceId}/collections/${collectionId}/records`);
  }

  _taskAsRecord(d) {
    const data = d.data();
    return {
      id: d.id,
      values: taskToRecordValues(data),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      _lastModifiedBy: data._lastModifiedBy,
    };
  }

  subscribeRecords(workspaceId, collectionId, callback) {
    if (!workspaceId || !collectionId) return () => {};
    const linked = this._linkedProject(collectionId);
    if (linked) {
      return onSnapshot(
        this._tasksCol(workspaceId, linked),
        (snap) => callback(snap.docs.map(d => this._taskAsRecord(d))),
        (err) => console.error('[subscribeRecords:linked]', err)
      );
    }
    return onSnapshot(
      this._recordsCol(workspaceId, collectionId),
      (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error('[subscribeRecords]', err)
    );
  }

  async getRecords(workspaceId, collectionId) {
    const linked = this._linkedProject(collectionId);
    if (linked) {
      const snap = await getDocs(this._tasksCol(workspaceId, linked));
      return snap.docs.map(d => this._taskAsRecord(d));
    }
    const snap = await getDocs(this._recordsCol(workspaceId, collectionId));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  async getRecordCount(workspaceId, collectionId) {
    const linked = this._linkedProject(collectionId);
    const snap = await getDocs(
      linked ? this._tasksCol(workspaceId, linked) : this._recordsCol(workspaceId, collectionId)
    );
    return snap.size;
  }

  async addRecord(workspaceId, collectionId, values) {
    const linked = this._linkedProject(collectionId);
    if (linked) {
      // Adding a row to a linked collection IS creating a task in the project.
      const projSnap = await getDoc(doc(db, 'workspaces', workspaceId, 'projects', linked));
      const statuses = projSnap.exists() && Array.isArray(projSnap.data().statuses)
        ? projSnap.data().statuses : undefined;
      const ref = await addDoc(this._tasksCol(workspaceId, linked), {
        ...recordValuesToNewTaskDoc(stripUndef(values || {}), statuses),
        userId: this.userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    }
    const ref = await addDoc(
      this._recordsCol(workspaceId, collectionId),
      { values: stripUndef(values), createdAt: serverTimestamp(), updatedAt: serverTimestamp() }
    );
    return ref.id;
  }

  async updateRecord(workspaceId, collectionId, recordId, values) {
    const linked = this._linkedProject(collectionId);
    if (linked) {
      await updateDoc(doc(this._tasksCol(workspaceId, linked), recordId), {
        ...recordPatchToTaskUpdate(stripUndef(values || {})),
        updatedAt: serverTimestamp(),
      });
      return;
    }
    await updateDoc(
      doc(db, `workspaces/${workspaceId}/collections/${collectionId}/records`, recordId),
      { values: stripUndef(values), updatedAt: serverTimestamp() }
    );
  }

  async patchRecord(workspaceId, collectionId, recordId, partialValues) {
    const linked = this._linkedProject(collectionId);
    if (linked) {
      // Dot-path update on the task doc — a t_status patch here is a real
      // status change and fires the project's automations, deliberately.
      await updateDoc(doc(this._tasksCol(workspaceId, linked), recordId), {
        ...recordPatchToTaskUpdate(stripUndef(partialValues || {})),
        updatedAt: serverTimestamp(),
      });
      return;
    }
    const ref = doc(db, `workspaces/${workspaceId}/collections/${collectionId}/records`, recordId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const existing = snap.data().values || {};
    await updateDoc(ref, {
      values: stripUndef({ ...existing, ...partialValues }),
      updatedAt: serverTimestamp(),
    });
  }

  async deleteRecord(workspaceId, collectionId, recordId) {
    const linked = this._linkedProject(collectionId);
    if (linked) {
      await deleteDoc(doc(this._tasksCol(workspaceId, linked), recordId));
      return;
    }
    await deleteDoc(
      doc(db, `workspaces/${workspaceId}/collections/${collectionId}/records`, recordId)
    );
  }
}

export const dataService = new DataService();